/**
 * @file useInactivityTimer.ts
 *
 * @description A custom hook that manages user inactivity and provides countdown functionality.
 * This hook is designed for security purposes in law enforcement applications,
 * allowing automatic logout or warning when a user has been inactive.
 * 
 * @module Shared/Hooks
 * @requires React
 */

import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Options for configuring the inactivity timer.
 * @interface InactivityTimerOptions
 */
interface InactivityTimerOptions {
  countdownDuration: number; // Milliseconds for countdown timer
  onTimeout: () => void; // Action when countdown completes
}

/**
 * @name useInactivityTimer
 *
 * @description A custom hook that manages user inactivity and provides countdown functionality.
 *
 * @param options - Configuration options for the inactivity timer
 *
 * @returns Object containing timer state and control functions
 */
export const useInactivityTimer = ({
  countdownDuration,
  onTimeout,
}: InactivityTimerOptions) => {
  /** Controls the visibility of the inactivity warning dialog */
  const [showWarning, setShowWarning] = useState(false);
  /** Time remaining in seconds before the onTimeout callback is triggered */
  const [remainingTime, setRemainingTime] = useState(countdownDuration / 1000); // Convert to seconds
  /** Reference to the countdown interval timer */
  const countdownTimerRef = useRef<number | null>(null);

  /**
   * Clears all active inactivity timers.
   */
  const clearInactivityTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  /**
   * Starts the countdown timer that displays to the user.
   * Shows the warning dialog and counts down until timeout.
   */
  const startCountdown = useCallback(() => {
    setShowWarning(true);
    setRemainingTime(countdownDuration / 1000);

    // Clear any existing countdown
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
    }

    // Start the countdown
    countdownTimerRef.current = window.setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInactivityTimer();
          onTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [countdownDuration, onTimeout, clearInactivityTimer]);

  /**
   * Starts or resets the inactivity timer.
   * This should be called whenever user activity is detected.
   */
  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();

    // Reset remaining time when starting a new timer
    setRemainingTime(countdownDuration / 1000);
    setShowWarning(false);
  }, [countdownDuration, clearInactivityTimer]);

  /**
   * Initialize the timer when the component mounts and
   * clean up on unmount to prevent memory leaks.
   */
  useEffect(() => {
    startInactivityTimer();

    // Cleanup on unmount
    return () => clearInactivityTimer();
  }, [startInactivityTimer, clearInactivityTimer]);

  /**
   * External control function to manually set the warning state.
   * If set to show, will start the countdown if not already running.
   *
   * @param isShowing - Whether to show or hide the warning
   */
  const setWarningState = useCallback(
    (isShowing: boolean) => {
      setShowWarning(isShowing);

      // If showing the warning, start the countdown
      if (isShowing && !countdownTimerRef.current) {
        startCountdown();
      }
    },
    [startCountdown]
  );

  return {
    /** Current time remaining in seconds before timeout */
    remainingTime,

    /** Whether the warning dialog should be displayed */
    showWarning,

    /** Function to manually control the warning dialog visibility */
    setWarningState,

    /** Function to clear all active timers */
    clearInactivityTimer,
  };
};
